import React from 'react';
import { connect } from 'react-redux';
import store2 from 'store2';
import { BatchSearchPeriodTypes } from '../../../../common/src/domain/batch/BatchSearchPeriodTypes';
import { BatchStates } from '../../../../common/src/enums/BatchStates';
import { getInspectionStandardTypeOptions } from '../../customerSettings';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchDelete, fetchGet } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { IRouterProps } from '../../utils/IRouterProps';
import { IconButton } from '../common/iconButtons/IconButton';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelDatePicker,
    EditPanelInput,
    EditPanelSection,
    EditPanelSelect,
    PanelBody,
    PanelRouterLink,
    PanelWithoutForm,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TableSortButton,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { BatchStatusLabel } from '../utils/BatchStatusLabel';
import { Component } from '../utils/Component';
import { ListPager } from '../utils/ListPager';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import RoleDependent from '../utils/RoleDependent';

interface IBatchListProps extends ITranslateProps, IRouterProps {
    dispatch: (prop: any) => void;
    location: any;
    userRole: Roles;
}

interface IValueText {
    value: string;
    text: string;
}

interface IBatch {
    id: string;
    clientCompanyName?: undefined | string;
    endDate?: undefined | Date;
    name: string;
    objectsCount: number;
    plannedEndDate?: undefined | Date;
    plannedStartDate?: undefined | Date;
    startDate?: undefined | Date;
    status?: undefined | BatchStates;
}

interface IBatchListState {
    isLoading: boolean;
    items: IBatch[];
    total: number;
    executorCompanies: IValueText[];
    searchParameters: {
        searchName?: undefined | string;
        sortName: boolean;
        sortPlanDate: boolean;
        inspectionStandardType?: undefined | string;
        executorCompanyId?: undefined | string;
        page: number;
        batchStatus?: undefined | string;
        plannedDateRangeStartDate?: undefined | string | null;
        plannedDateRangeEndDate?: undefined | string | null;
        plannedDateRangeType?: undefined | string | null;
    };
}

function mapStateToProps(state: any) {
    const { authentication } = state;
    return {
        userRole: authentication.role,
    };
}

const storeKey = 'BatchesList';
const itemsPerPage = 50;

class BatchesList extends Component<IBatchListProps, IBatchListState> {
    constructor(props: IBatchListProps) {
        super(props);

        this.state = {
            isLoading: false,
            items: [],
            executorCompanies: [],
            total: 0,
            searchParameters: {
                page: 1,
                sortName: false,
                sortPlanDate: false,
            },
        };
    }

    public override async componentDidMount() {
        if (store2.get(storeKey)) {
            await this.setState({ searchParameters: store2.get(storeKey) });
        }
        await this.loadCompanies();
        await this.search(this.props.location.query.page);
    }

    public override render() {
        return (
            <div>
                <Topbar>
                    <TopbarLeft>
                        <TopbarCrumbActive title="Batches" icon="fa-briefcase" />
                    </TopbarLeft>
                    <RoleDependent
                        allowed={[Roles.Administrator, Roles.Client]}
                        content={
                            <TopbarRight>
                                <PanelRouterLink to="/batches/add" text={this.props.t('common.addItem', 'Batch')} icon="fa fa-plus" small={true} />
                            </TopbarRight>
                        }
                    />
                </Topbar>
                <Content>
                    <Tray>
                        <PanelWithoutForm>
                            <PanelBody noPadding={true}>
                                <Table hover={true}>
                                    <TableHead>
                                        <TableHeadRow light={true}>
                                            <TableHeader>
                                                {this.props.t('common.name')} <TableSortButton onClick={async () => this.sortName()} />
                                            </TableHeader>
                                            <th colSpan={2}>
                                                {this.props.t('batch.plannedDate')} <TableSortButton onClick={async () => this.sortPlanDate()} />
                                            </th>
                                            {this.props.userRole === Roles.Administrator && (
                                                <TableHeader>{this.props.t('common.client')}</TableHeader>
                                            )}
                                            <TableHeader>{this.props.t('common.status')}</TableHeader>
                                            <TableHeader>{this.props.t('batch.objectCount')}</TableHeader>
                                            <TableHeader>&nbsp; </TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    {(this.state.isLoading && (
                                        <TableBody>
                                            <TableRow>
                                                <TableData colSpan={6}>
                                                    <Spinner />
                                                </TableData>
                                            </TableRow>
                                        </TableBody>
                                    )) || (
                                        <TableBody>
                                            {this.state.items.map((item) => (
                                                <TableRow key={item.id} onClick={() => this.rowClicked(item.id)}>
                                                    <TableData> {item.name} </TableData>
                                                    <TableData nowrap>
                                                        <NullableFormattedDate value={item.plannedStartDate} />{' '}
                                                    </TableData>
                                                    <TableData nowrap>
                                                        {' '}
                                                        <NullableFormattedDate value={item.plannedEndDate} />{' '}
                                                    </TableData>
                                                    {this.props.userRole === Roles.Administrator && <TableData> {item.clientCompanyName} </TableData>}
                                                    <TableData>
                                                        {' '}
                                                        <BatchStatusLabel value={item.status} />
                                                    </TableData>
                                                    <TableData> {item.objectsCount} </TableData>
                                                    <TableButtons>
                                                        <RoleDependent
                                                            allowed={[Roles.Administrator, Roles.Client]}
                                                            content={
                                                                <>
                                                                    <IconButton
                                                                        iconClass="fa-globe"
                                                                        onClick={() => this.props.navigate(`/batches/map?batchId=${item.id}`)}
                                                                    />
                                                                    <IconEditButton onClick={() => this.props.navigate(`/batches/${item.id}/edit`)} />
                                                                    {(item.status === BatchStates.Open || item.status === BatchStates.Proposal) && (
                                                                        <IconRemoveButton
                                                                            removeQuestion={this.props.t('common.confirmDelete', item.name)}
                                                                            onClick={async () => this.remove(item.id)}
                                                                        />
                                                                    )}
                                                                </>
                                                            }
                                                        />
                                                    </TableButtons>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    )}
                                </Table>
                            </PanelBody>
                            <ListPager
                                disabled={this.state.isLoading}
                                itemsPerPage={itemsPerPage}
                                total={this.state.total}
                                page={this.state.searchParameters.page}
                                handleNavigation={this.handleNavigation.bind(this)}
                            />
                        </PanelWithoutForm>
                    </Tray>

                    <aside className="tray tray-right tray290" data-tray-height="match">
                        <form
                            id="filter"
                            onSubmit={(e) => {
                                e.preventDefault();
                                void this.search();
                            }}
                        >
                            <div className="admin-form">
                                <h4> {this.props.t('common.findItem', 'Batch')}</h4>
                                <EditPanelSection topBorder={true}>
                                    <EditPanelInput
                                        name="searchName"
                                        placeholder={this.props.t('common.name')}
                                        value={this.state.searchParameters.searchName}
                                        icon="fa fa-briefcase"
                                        handleChange={this.handleChange.bind(this)}
                                    />
                                </EditPanelSection>

                                <RoleDependent
                                    allowed={[Roles.Administrator, Roles.Client]}
                                    content={
                                        <>
                                            <EditPanelSection>
                                                <EditPanelSelect
                                                    name="inspectionStandardType"
                                                    value={this.state.searchParameters.inspectionStandardType}
                                                    handleChange={this.handleChange.bind(this)}
                                                    disabled={this.state.isLoading}
                                                    placeholder={this.props.t('common.selectItem', this.props.t('common.inspectionType'))}
                                                    options={getInspectionStandardTypeOptions(this.props.t)}
                                                />
                                            </EditPanelSection>
                                            <EditPanelSection>
                                                <EditPanelSelect
                                                    name="batchStatus"
                                                    value={this.state.searchParameters.batchStatus}
                                                    handleChange={this.handleChange.bind(this)}
                                                    disabled={this.state.isLoading}
                                                    placeholder={this.props.t('common.selectItem', this.props.t('common.status'))}
                                                    options={[
                                                        ...[
                                                            { value: BatchStates.Open, text: this.props.t(`batchStatus.${BatchStates.Open}`) },
                                                            { value: BatchStates.Active, text: this.props.t(`batchStatus.${BatchStates.Active}`) },
                                                            {
                                                                value: BatchStates.Completed,
                                                                text: this.props.t(`batchStatus.${BatchStates.Completed}`),
                                                            },
                                                            {
                                                                value: BatchStates.Proposal,
                                                                text: this.props.t(`batchStatus.${BatchStates.Proposal}`),
                                                            },
                                                        ],
                                                        ...(this.props.userRole === Roles.Administrator
                                                            ? [
                                                                  {
                                                                      value: BatchStates.Deleted,
                                                                      text: this.props.t(`batchStatus.${BatchStates.Deleted}`),
                                                                  },
                                                              ]
                                                            : []),
                                                    ]}
                                                />
                                            </EditPanelSection>
                                            <EditPanelSection>
                                                <EditPanelSelect
                                                    name="executorCompanyId"
                                                    value={this.state.searchParameters.executorCompanyId}
                                                    handleChange={this.handleChange.bind(this)}
                                                    disabled={this.state.isLoading}
                                                    placeholder={this.props.t('common.selectItem', this.props.t('batch.executor'))}
                                                    options={this.state.executorCompanies}
                                                />
                                            </EditPanelSection>
                                        </>
                                    }
                                />

                                <EditPanelSection>
                                    <h5>{this.props.t('batch.plannedDate')}</h5>
                                    <EditPanelSelect
                                        name="plannedDateRangeType"
                                        value={this.state.searchParameters.plannedDateRangeType}
                                        handleChange={this.handleChange.bind(this)}
                                        disabled={this.state.isLoading}
                                        placeholder={this.props.t('common.selectItem', this.props.t('common.period'))}
                                        options={[
                                            {
                                                text: this.props.t(`batchSearchPeriodTypes.${BatchSearchPeriodTypes.ActiveInPeriod}`),
                                                value: BatchSearchPeriodTypes.ActiveInPeriod,
                                            },
                                            {
                                                text: this.props.t(`batchSearchPeriodTypes.${BatchSearchPeriodTypes.StartsInPeriod}`),
                                                value: BatchSearchPeriodTypes.StartsInPeriod,
                                            },
                                            {
                                                text: this.props.t(`batchSearchPeriodTypes.${BatchSearchPeriodTypes.EndsInPeriod}`),
                                                value: BatchSearchPeriodTypes.EndsInPeriod,
                                            },
                                        ]}
                                    />
                                </EditPanelSection>

                                {this.state.searchParameters.plannedDateRangeType && (
                                    <>
                                        <EditPanelSection>
                                            <EditPanelDatePicker
                                                name="plannedDateRangeStartDate"
                                                handleChange={this.handleChange.bind(this)}
                                                value={this.state.searchParameters.plannedDateRangeStartDate}
                                                disabled={this.state.isLoading}
                                                placeholder={this.props.t('common.startDate')}
                                            />
                                        </EditPanelSection>
                                        <EditPanelSection>
                                            <EditPanelDatePicker
                                                name="plannedDateRangeEndDate"
                                                handleChange={this.handleChange.bind(this)}
                                                value={this.state.searchParameters.plannedDateRangeEndDate}
                                                disabled={this.state.isLoading}
                                                placeholder={this.props.t('common.endDate')}
                                            />
                                        </EditPanelSection>
                                    </>
                                )}

                                <EditPanelButtons topBorder={true}>
                                    <EditPanelButton
                                        onClick={async () => this.resetForm()}
                                        text={this.props.t('common.reset')}
                                        disabled={this.state.isLoading}
                                    />
                                    <EditPanelButton
                                        onClick={async () => this.search()}
                                        text={this.props.t('common.search')}
                                        primary
                                        disabled={this.state.isLoading}
                                    />
                                </EditPanelButtons>
                            </div>
                        </form>
                    </aside>
                </Content>
            </div>
        );
    }

    private async remove(id: string) {
        const url = `/api/batches/${id}`;
        await fetchDelete(url);
        await this.search(this.props.location.query.page);
    }

    private handleNavigation(page: number) {
        this.props.navigate({ pathname: '/batches', search: `?page=${page}` });
        void this.search(page.toString());
    }

    private async loadValueText(url: string, stateKey: string) {
        const result = await fetchGet(url);
        const items = result.json.items ? result.json.items : result.json;
        const keyValueArray = items.map((item: any) => ({ value: item.id, text: item.name }));
        const newState = {} as any;
        newState[stateKey] = keyValueArray;
        await this.setState(newState);
    }

    private async handleChange(value: any, stateKey: string) {
        const newState: any = this.state.searchParameters;
        newState[stateKey] = value;
        await this.setState({ searchParameters: newState });

        if (stateKey !== 'searchName') {
            void this.search();
        }
    }

    private async loadCompanies() {
        await this.loadValueText('/api/companyList?type=surveyor|operator', 'executorCompanies');
    }

    private rowClicked(batchId: string) {
        const url = `/batches/${batchId}`;
        this.props.navigate(url);
    }

    private async sortName() {
        await this.setState({
            searchParameters: { ...this.state.searchParameters, ...{ sortName: !this.state.searchParameters.sortName, sortPlanDate: false } },
        });
        void this.search();
    }

    private async sortPlanDate() {
        await this.setState({
            searchParameters: { ...this.state.searchParameters, ...{ sortPlanDate: !this.state.searchParameters.sortPlanDate, sortName: false } },
        });
        void this.search();
    }

    private async resetForm() {
        await this.setState({
            searchParameters: {
                ...this.state.searchParameters,
                ...{
                    searchName: undefined,
                    sortPlanDate: false,
                    inspectionStandardType: undefined,
                    executorCompanyId: undefined,
                    batchStatus: undefined,
                    plannedDateRangeStartDate: undefined,
                    plannedDateRangeEndDate: undefined,
                    plannedDateRangeType: undefined,
                },
            },
        });
        void this.search();
    }

    private async search(pageString?: string) {
        const pageNumber = pageString ? parseInt(pageString, 10) : 1;
        await this.setState({ isLoading: true, searchParameters: { ...this.state.searchParameters, ...{ page: pageNumber } } });

        store2.set(storeKey, this.state.searchParameters);

        const result = await fetchGet('/api/batches', {
            limit: itemsPerPage.toString(),
            offset: (itemsPerPage * (pageNumber - 1)).toString(),
            status: this.state.searchParameters.batchStatus ? this.state.searchParameters.batchStatus : undefined,
            name: this.state.searchParameters.searchName ? this.state.searchParameters.searchName : undefined,
            executorCompanyId: this.state.searchParameters.executorCompanyId ? this.state.searchParameters.executorCompanyId : undefined,
            inspectionStandardType: this.state.searchParameters.inspectionStandardType
                ? this.state.searchParameters.inspectionStandardType
                : undefined,
            sortname: this.state.searchParameters.sortName ? 'true' : 'false',
            sortplandate: this.state.searchParameters.sortPlanDate ? 'true' : 'false',
            plannedDateRangeType: this.state.searchParameters.plannedDateRangeType ? this.state.searchParameters.plannedDateRangeType : undefined,
            plannedDateRangeStartDate: this.state.searchParameters.plannedDateRangeStartDate
                ? this.state.searchParameters.plannedDateRangeStartDate
                : undefined,
            plannedDateRangeEndDate: this.state.searchParameters.plannedDateRangeEndDate
                ? this.state.searchParameters.plannedDateRangeEndDate
                : undefined,
        });
        await this.setState({ total: result.json.total, items: result.json.items, isLoading: false });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(BatchesList as any) as any);
